<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div
    class="modal geofence-list-selection-modal px-4"
    :class="className">
    <div
      class="modal-dialog modal-full-width modal-full-height modal-max-height"
      role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ capitalize($t('messages.selectPlaces')) }}</h5>
          <button
            type="button"
            class="close"
            data-dismiss="modal"
            aria-label="Close"
            @click="cancel">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body flex-v">
          <div class="place-attributes-header">
            <div class="place-attributes">
              <div class="filters-header">
                <icon-colored-filter />
                <div class="text">
                  {{ capitalize($t('names.filter', { n: 2 })) }}
                </div>
              </div>
              <div class="place-attribute">
                <div class="attribute-group-title">{{ $t('messages.nameAndAddress') }}:</div>
                <input
                  v-model="searchTerm"
                  class="form-control text-search-input"
                  type="text"
                  :placeholder="capitalize($t('messages.searchInNamesAndAddresses'))" />
              </div>
              <div
                v-for="placeAttribute in placeAttributes"
                :key="placeAttribute.id"
                class="place-attribute">
                <div class="attribute-group-title">{{ capitalize(placeAttribute.group) }}:</div>
                <multiselect
                  v-model="placeAttribute.selections"
                  track-by="id"
                  label="title"
                  :options="placeAttribute.values"
                  :searchable="true"
                  :allow-empty="true"
                  :show-labels="false"
                  :close-on-select="false"
                  :multiple="true"
                  :placeholder="capitalize($t('messages.pickOneOrMoreOptional'))"
                  @select="
                    (x) => {
                      filtersSelected(placeAttribute, x);
                    }
                  "
                  @remove="
                    (x) => {
                      filtersRemoved(placeAttribute, x);
                    }
                  " />
              </div>
            </div>

            <GeofenceEstimatedDeliveryStats
              :unit-clusters="unitClusters"
              :geofences="geofences" />
          </div>

          <geofence-list-map-selector
            ref="geofenceListMapSelector"
            :geofences="filteredPlaceGeofences"
            :requested-for-utc="requestedForUtc"
            @delete-geofence="handleDeleteGeofence"
            @select-geofence="handleSelectGeofence"
            @unselect-geofence="handleUnselectGeofence"
            @unit-clusters-changed="unitClustersChanged" />
          <div class="flex-v geofence-options">
            <div class="main">
              <div
                v-if="selectedGeofenceMode === GeofenceMode.Radius"
                class="radius-section"
                :class="{
                  visible:
                    selectedPointPlaceGeofences.length > 0 ||
                    (selectedPolygonPlaceGeofences.length > 0 && !importPolygons),
                }">
                <p>{{ $t('messages.defaultRadiusForPlacesWithoutPolygons') }}:</p>
                <div>
                  <radius-distance
                    v-if="isVisible"
                    :radius-distance-in-meters="radiusDistance * 1000"
                    :use-horizontal-layout="true"
                    @radius-changed="handleRadiusChange" />
                </div>
              </div>

              <div
                v-if="selectedGeofenceMode === GeofenceMode.Dynamic"
                class="radius-section dynamic-geofences"
                :class="{
                  visible:
                    selectedPointPlaceGeofences.length > 0 ||
                    (selectedPolygonPlaceGeofences.length > 0 && !importPolygons),
                }">
                <DynamicGeofences
                  ref="dynamicGeofences"
                  :geofences="placeGeofences"
                  :map-instance="mapRef?.map"
                  :horizontal="true"
                  :is-handling-drawing="true"
                  @on-polygons-loading="onDynamicGeofenceLoadingChanged"
                  @on-dynamic-geofence-updated="onGeofenceUpdated" />
              </div>

              <div
                v-if="canUseDynamicGeofences"
                class="actions">
                <a
                  v-if="selectedGeofenceMode === GeofenceMode.Dynamic"
                  @click="onGeofenceModeClick(GeofenceMode.Radius)">
                  <Radius />
                  <small>{{ $t('messages.changeToRadiusGeofences') }}</small>
                </a>
                <a
                  v-show="selectedGeofenceMode === GeofenceMode.Radius"
                  @click="onGeofenceModeClick(GeofenceMode.Dynamic)">
                  <span v-tooltip.right="$t('messages.betaFeature', [capitalize($t('messages.dynamicGeofences'))])">
                    <SimpleDynamicGeofence />
                    <small
                      >{{ $t('messages.changeToDynamicGeofence') }}
                      <span class="badge badge-info ml-3">beta</span></small
                    >
                  </span>
                </a>
              </div>
            </div>

            <template v-if="selectedPolygonPlaceGeofences.length > 0">
              <div class="separator" />
              <div class="import-polygons-section scale-vertical">
                <div class="label">{{ capitalize($t('messages.importPolygons')) }}?</div>
                <toggle-button
                  :color="colors.success"
                  :value="importPolygons"
                  :labels="{ checked: capitalize($t('actions.yes')), unchecked: capitalize($t('actions.no')) }"
                  :sync="true"
                  @change="
                    () => {
                      importPolygons = !importPolygons;
                    }
                  " />
              </div>
            </template>
          </div>
          <unbranded-loader
            v-if="showDynamicGeofenceLoadingMessage"
            :message="$t('messages.loadingGeofencesSeveralMinutes')" />
        </div>
        <div class="modal-footer">
          <button
            id="add_geofences_btn"
            type="button"
            class="btn btn-primary"
            @click="confirm">
            {{ $t('actions.ok') }}
          </button>
          <button
            type="button"
            class="btn btn-default"
            data-dismiss="modal"
            @click="cancel">
            {{ $t('actions.cancel') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

  import { DateTime } from 'luxon';
  import { capitalize } from 'vue';
  import { mapActions, mapState } from 'vuex';

  import Colors from '@/Colors';
  import DynamicGeofences from '@/common/components/controls/dynamic-geofences/DynamicGeofences.vue';
  import GeofenceMode from '@/common/components/controls/dynamic-geofences/enums/GeofenceMode.enum';
  import SimpleDynamicGeofence from '@/common/components/controls/dynamic-geofences/icons/SimpleDynamicGeofence.vue';
  import GeofenceListMapSelector from '@/common/components/controls/GeofenceListMapSelector.vue';
  import UnbrandedLoader from '@/common/components/UnbrandedLoader.vue';
  import { IconColoredFilter } from '@/common/icons/colored';
  import Radius from '@/common/icons/monochrome/Radius.vue';
  import Geofence from '@/common/models/geo/Geofence';
  import { areArraysEqual } from '@/globals';
  import GeofenceEstimatedDeliveryStats from '@/org-app/components/geofenceGroups/GeofenceEstimatedDeliveryStats.vue';

  import RadiusDistance from './RadiusDistance.vue';

  export default {
    components: {
      GeofenceEstimatedDeliveryStats,
      Radius,
      DynamicGeofences,
      SimpleDynamicGeofence,
      GeofenceListMapSelector,
      IconColoredFilter,
      RadiusDistance,
      UnbrandedLoader,
    },
    props: {
      isVisible: {
        type: Boolean,
        default: false,
      },
      placeGeofences: {
        type: Array,
      },
      requestedForUtc: {
        type: DateTime,
      },
    },
    data() {
      return {
        colors: Colors,
        deletedPlaceGeofences: [],
        showDynamicGeofenceLoadingMessage: false,
        importPolygons: true,
        placeAttributes: [],
        radiusDistance: 0.01,
        searchTerm: '',
        selectedAttributes: [],
        shouldHideDuplicateAddresses: true,
        selectedGeofenceMode: GeofenceMode.Radius,
        GeofenceMode,
        mapRef: null,
        geofences: [],
        unitClusters: [],
      };
    },
    computed: {
      ...mapState('orgApp', {
        organizationSettings: (state) => state.organizationSettings,
      }),
      activePlaceAttributes() {
        return this.placeAttributes.filter((a) => a.selections.length > 0);
      },
      className() {
        if (this.isVisible) {
          return 'visible';
        }
        return 'fade';
      },
      selectedPlaceGeofences() {
        const selection =
          this.filteredPlaceGeofences !== null ? this.filteredPlaceGeofences.filter((x) => x.isSelected) : [];
        return selection;
      },
      selectedPointPlaceGeofences() {
        return this.selectedPlaceGeofences.filter((x) => x.isPoint);
      },
      selectedPolygonPlaceGeofences() {
        return this.selectedPlaceGeofences.filter((x) => !x.isPoint);
      },
      areAllGeofencesSelected() {
        return (
          this.selectedPlaceGeofences.length > 0 &&
          this.selectedPlaceGeofences.length === this.filteredPlaceGeofences.length
        );
      },
      filteredPlaceGeofences() {
        let geofences = this.placeGeofences
          .filter(
            (x) =>
              this.searchTerm == null ||
              this.searchTerm.trim() === '' ||
              x.name.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1 ||
              x.description.toLowerCase().indexOf(this.searchTerm.toLowerCase()) > -1,
          )
          .filter((x) => this.deletedPlaceGeofences.indexOf(x) === -1)
          .sort((a, b) => a.name.localeCompare(b.name));
        if (this.shouldHideDuplicateAddresses) {
          geofences = geofences.reduce((mem, g) => {
            if (mem.find((x) => x.description === g.description || (x.lat === g.lat && x.lng === g.lng)) == null) {
              return [...mem, g];
            }
            return mem;
          }, []);
        }
        if (this.activePlaceAttributes.length === 0) {
          return geofences;
        }
        return geofences.filter((g) => {
          return (
            this.activePlaceAttributes.filter((pa) => {
              const matchingGroup = g.attributes.find((a) => a.group === pa.group);
              if (matchingGroup == null) {
                return false;
              }
              return pa.selections.filter((s) => matchingGroup.values.find((v) => v.id === s.id) != null).length > 0;
            }).length > 0
          );
        });
      },
      canUseDynamicGeofences() {
        return this.organizationSettings.uiEnableDynamicGeofences;
      },
    },
    methods: {
      ...mapActions('orgApp', ['getEstimatedDeliveryStatistics', 'getUnits']),
      capitalize(x) {
        return capitalize(x);
      },
      confirm() {
        const places = [...this.selectedPlaceGeofences].map((place) => {
          if (this.selectedGeofenceMode === GeofenceMode.Dynamic) {
            const { data } = this.$refs.dynamicGeofences.isochronesResult.find((x) => x.geofenceId === place.id);

            return Geofence.fromObject({
              ...place,
              dynamicGeofences: {
                params: this.$refs.dynamicGeofences.params,
                featureCollection: data,
              },
              geoJson: data.features[0].geometry,
            });
          }
          return place;
        });

        if (!this.importPolygons) {
          places.forEach((p) => {
            p.fromGeofences = [];
            p.fromPlaceGeofences = [];
            p.geoJson = null;
            p.tags = p.tags.replace('"isPoint":false', '"isPoint":true');
          });
        }
        this.$emit('confirmed', { places });
      },
      cancel() {
        this.$emit('cancelled');
      },
      handleRadiusChange(radius, radiusUnit, radiusInUnit) {
        this.radiusDistance = radius / 1000;
        this.placeGeofences.forEach((x) => {
          x.radius = this.radiusDistance;
          x.radiusUnit = radiusUnit;
          x.radiusInUnit = radiusInUnit;
        });
        this.geofences = [...this.placeGeofences];
        this.$refs.geofenceListMapSelector.refresh();
      },
      onGeofenceUpdated(updatedGeofence) {
        const updatedGeofences = [...this.geofences].map((g) => {
          if (g.id === updatedGeofence.id) {
            return Geofence.fromObject(updatedGeofence, null);
          }
          return Geofence.fromObject(g, null);
        });
        this.geofences = updatedGeofences;
      },
      onDynamicGeofenceLoadingChanged(value) {
        this.showDynamicGeofenceLoadingMessage = value;
      },
      handleDeleteGeofence(g) {
        this.deletedPlaceGeofences.push(g);
        this.$refs.geofenceListMapSelector.refresh();
      },
      handleSelectGeofence(g) {
        const geofence = this.placeGeofences.find((x) => x.id === g.id);
        if (geofence) {
          geofence.isSelected = true;
        }
        this.$refs.geofenceListMapSelector.refresh();
      },
      handleUnselectGeofence(g) {
        const geofence = this.placeGeofences.find((x) => x.id === g.id);
        if (geofence) {
          geofence.isSelected = false;
        }
        this.$refs.geofenceListMapSelector.refresh();
      },
      processPlaceAttributes() {
        const attributes = [];
        const geofencesImportedFromHereApi = this.filteredPlaceGeofences.filter(
          (x) => x.placeSearchProvider === 'Here',
        );
        if (geofencesImportedFromHereApi.length > 0) {
          geofencesImportedFromHereApi.forEach((g) => {
            const hereApiObject = JSON.parse(g.placeSearchJson);
            if (hereApiObject.tags != null) {
              hereApiObject.tags.forEach((t) => {
                const group = attributes.find((a) => a.group === t.group);
                if (group == null) {
                  attributes.push({
                    group: t.group,
                    values: [{ id: t.id, title: t.title }],
                    selections: [],
                  });
                } else if (group.values.find((x) => x.id === t.id) == null) {
                  group.values.push({ id: t.id, title: t.title });
                }
              });
            }
            if (hereApiObject.category != null) {
              const group = attributes.find((a) => a.group === 'category');
              if (group == null) {
                attributes.push({
                  group: 'category',
                  values: [{ id: hereApiObject.category.id, title: hereApiObject.category.title }],
                  selections: [],
                });
              } else if (group.values.find((x) => x.id === hereApiObject.category.id) == null) {
                group.values.push({ id: hereApiObject.category.id, title: hereApiObject.category.title });
              }
            }
          });
        }
        this.placeAttributes = attributes;
        this.$emit('hideLoader');
      },
      filtersSelected() {
        this.$refs.geofenceListMapSelector.refresh();
      },
      filtersRemoved() {
        this.$refs.geofenceListMapSelector.refresh();
      },
      onGeofenceModeClick(mode) {
        this.selectedGeofenceMode = mode;

        if (mode === GeofenceMode.Radius) {
          this.$refs.dynamicGeofences.clearDynamicGeofencesLayers();
          this.handleRadiusChange(10);
        }

        if (mode === GeofenceMode.Dynamic) {
          this.handleRadiusChange(0);
        }
      },
      unitClustersChanged(unitClusters) {
        if (areArraysEqual(this.unitClusters, unitClusters)) {
          return;
        }

        this.unitClusters = unitClusters;
      },
    },
    mounted() {
      this.processPlaceAttributes();
      this.geofences = [...this.placeGeofences];

      this.mapRef = this.$refs?.geofenceListMapSelector?.map;
    },
  };
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  #app {
    .geofence-list-selection-modal {
      hr {
        width: 100%;
      }

      .radius-section {
        transition:
          opacity 0.25s ease-in-out,
          transform 0.25s ease-out;
        height: 0;
        opacity: 0;
        transform: scaleY(0);
        flex-grow: 1;

        &.visible {
          transition:
            opacity 0.25s ease-in-out,
            transform 0.25s ease-out;
          height: auto;
          opacity: 1;
          transform: scaleY(1);
        }

        &.dynamic-geofences {
          margin-top: 2rem;
        }
      }

      .separator {
        width: 0.1rem;
        background: $borders;
        margin: 0 1.6rem;
        height: 100%;
      }

      .import-polygons-section {
        .label {
          margin-bottom: 1rem;
        }
      }

      .place-attributes-header {
        display: flex;
      }

      .place-attributes {
        display: flex;
        flex: 1;
        flex-wrap: wrap;
        margin-bottom: 1rem;

        .filters-header {
          display: flex;
          margin-right: 1.6rem;
          text-transform: uppercase;
          font-weight: 600;
          border-right: solid 1px #ccc;
          padding-right: 1.6rem;
          align-items: center;

          svg {
            width: 2.4rem;
            height: 2.4rem;
            margin-right: 0.6rem;
          }
        }
        .place-attribute {
          display: flex;
          flex-direction: column;
          margin-right: 0.8rem;
          justify-content: center;

          .attribute-group-title {
            text-transform: capitalize;
            font-size: 0.9em;
            margin-bottom: 0.3rem;
            font-weight: 500;
          }

          .multiselect {
          }

          .text-search-input {
            width: 21rem;
          }
        }
      }

      .flex-h {
        flex-shrink: 0;
      }

      .side {
        display: flex;
        flex-direction: row;
        justify-content: inherit;

        button {
          background: transparent;
          border: 1px solid var(--defaultButtonBorderColor);
          margin: 0;
          outline: none;
          display: flex;
          align-items: center;
          cursor: pointer;
          border-radius: 0 0 1rem 1rem;
          padding: 0.5rem 1.5rem;

          &:hover,
          &.selected {
            background: var(--primaryColor);
            color: var(--primaryColorReverse);

            svg {
              fill: var(--primaryColorReverse);
              stroke: var(--primaryColorReverse);
            }
          }

          svg {
            width: 1.2rem;
            position: relative;
            top: 2px;
            margin-right: 1rem;
          }
        }
      }

      .main {
        flex: 1;

        .actions {
          display: flex;
          flex-direction: column;

          a {
            display: flex;
            flex-direction: row;
            align-items: center;
            cursor: pointer;
            font-weight: var(--linkFontWeight);
            color: var(--secondaryColor);

            svg {
              width: 1.6rem;
              position: relative;
              fill: var(--actionsColor);
              stroke: var(--actionsColor);
              margin-right: 0.5rem;
            }

            span,
            small {
              display: flex;
              flex-direction: row;
              align-items: center;
              justify-content: center;
              font-size: 1.2rem !important;
            }

            &:not(:last-child) {
              margin-bottom: 1rem;
            }
          }
        }
      }

      svg {
        stroke: var(--textColor);
      }
    }

    .unit-details-card {
      position: absolute;
    }
  }
</style>
